import React from 'react';
import { Card } from '../components/UI';
import GrowthChart from '../components/GrowthChart';
import { calculateAge } from '../helpers/utils';
import { immunizationSchedule } from '../helpers/immunizationSchedule';
import { calculateZScore } from '../helpers/growthEvaluation';

const SummaryItem = ({ label, value, icon, color = 'text-brand-dark' }) => (
    <div className="text-center p-3 bg-gray-50 rounded-lg">
        <div className={`mx-auto h-10 w-10 flex items-center justify-center rounded-full bg-white text-2xl ${color}`}>{icon}</div>
        <p className={`mt-2 text-xl font-bold ${color}`}>{value}</p>
        <p className="text-xs text-brand-muted">{label}</p>
    </div>
);

const TimelineItem = ({ assessment }) => {
    const status = assessment.overallStatus || 'Selesai';
    let borderColor = 'border-gray-300';
    if (status === 'Sesuai') borderColor = 'border-green-500';
    if (status === 'Meragukan') borderColor = 'border-yellow-500';
    if (status === 'Ada Penyimpangan') borderColor = 'border-red-500';

    return (
        <div className="flex items-start space-x-4">
            <div className={`mt-1 flex-shrink-0 w-4 h-4 rounded-full border-4 ${borderColor} bg-white`}></div>
            <div>
                <p className="font-semibold text-brand-dark">{assessment.createdAt.toDate().toLocaleDateString('id-ID', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
                <p className="text-sm text-brand-muted">Status: <span className="font-medium text-brand-dark">{status}</span></p>
            </div>
        </div>
    );
};

export default function ReportCardScreen({ user, child, assessments, immunizations }) {
    const latestGrowth = assessments.find(a => a.pertumbuhan)?.pertumbuhan;
    const completedImmunizations = Object.values(immunizations).filter(i => i.done).length;
    
    let giziStatus = 'N/A';
    let giziColor = 'text-brand-dark';
    if (latestGrowth) {
        const zScore = calculateZScore(parseFloat(latestGrowth.bb), child.ageInMonths);
        if (zScore < -3) { giziStatus = 'Sangat Kurang'; giziColor = 'text-red-600'; }
        else if (zScore < -2) { giziStatus = 'Kurang'; giziColor = 'text-yellow-600'; }
        else if (zScore <= 2) { giziStatus = 'Normal'; giziColor = 'text-green-600'; }
        else { giziStatus = 'Lebih'; giziColor = 'text-red-600'; }
    }

    return (
        <div className="space-y-6 pb-20">
            <Card>
                <div className="text-center">
                    <h2 className="text-2xl font-bold text-brand-dark">{child.name}</h2>
                    <p className="text-brand-muted">Rapor Kesehatan Tumbuh Kembang - Usia {calculateAge(child.dob.toDate())}</p>
                </div>
            </Card>

            <Card>
                <h3 className="text-xl font-bold text-brand-dark mb-4">Ringkasan Status</h3>
                <div className="grid grid-cols-3 gap-3">
                    <SummaryItem label="Status Gizi" value={giziStatus} icon="⚖️" color={giziColor} />
                    <SummaryItem label="Imunisasi" value={`${completedImmunizations}/${immunizationSchedule.length}`} icon="💉" color="text-green-600" />
                    <SummaryItem label="Pemeriksaan" value={assessments.length} icon="📋" />
                </div>
            </Card>

            {assessments.length > 1 && (
                <Card>
                    <GrowthChart assessments={assessments} childGender={child.gender} />
                </Card>
            )}

            <Card>
                <h3 className="text-xl font-bold text-brand-dark mb-4">Linimasa Pemeriksaan</h3>
                <div className="space-y-4 border-l-2 border-gray-200 ml-2">
                    {assessments.length > 0 ? assessments.map(asmnt => (
                        <div key={asmnt.id} className="relative pl-8">
                             <TimelineItem assessment={asmnt} />
                        </div>
                    )) : <p className="text-brand-muted pl-4">Belum ada riwayat pemeriksaan.</p>}
                </div>
            </Card>
        </div>
    );
}
